import { useState, useEffect, useMemo, useRef } from "react";
import { database } from "../firebase/config";

const stableSerialize = (value) => {
  const seen = new WeakSet();

  const normalize = (v) => {
    if (v === undefined) return { __undefined__: true };
    if (v === null) return null;
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    )
      return v;

    if (v instanceof Date) return { __dateMs__: v.getTime() };

    if (Array.isArray(v)) return v.map(normalize);

    if (v && typeof v === "object") {
      if (
        typeof v.toDate === "function" &&
        typeof v.seconds === "number" &&
        typeof v.nanoseconds === "number"
      ) {
        return { __timestamp__: [v.seconds, v.nanoseconds] };
      }

      if (seen.has(v)) return { __circular__: true };
      seen.add(v);

      const out = {};
      Object.keys(v)
        .sort()
        .forEach((k) => {
          out[k] = normalize(v[k]);
        });
      return out;
    }

    return String(v);
  };

  return JSON.stringify(normalize(value));
};

export const useCollection = (
  collection,
  _query,
  _ordine,
  _limit,
  _realtime = true,
) => {
  const [documents, setDocuments] = useState(null);
  const [error, setError] = useState(null);

  const queryKey = useMemo(() => stableSerialize(_query ?? null), [_query]);
  const ordineKey = useMemo(() => stableSerialize(_ordine ?? null), [_ordine]);
  const limitKey = useMemo(() => stableSerialize(_limit ?? null), [_limit]);
  const realtimeKey = useMemo(
    () => stableSerialize(_realtime ?? true),
    [_realtime],
  );

  const queryRef = useRef(_query ?? null);
  const ordineRef = useRef(_ordine ?? null);
  const limitRef = useRef(_limit ?? null);
  const realtimeRef = useRef(_realtime ?? true);

  useEffect(() => {
    queryRef.current = _query ?? null;
  }, [_query]);

  useEffect(() => {
    ordineRef.current = _ordine ?? null;
  }, [_ordine]);

  useEffect(() => {
    limitRef.current = _limit ?? null;
  }, [_limit]);

  useEffect(() => {
    realtimeRef.current = _realtime ?? true;
  }, [_realtime]);

  useEffect(() => {
    if (!collection) {
      setDocuments(null);
      setError(null);
      return () => {};
    }

    let ref = database.collection(collection);
    const query = queryRef.current;
    const ordine = ordineRef.current;
    const limit = limitRef.current;
    const realtime = realtimeRef.current;

    try {
      if (query) {
        if (Array.isArray(query[0])) {
          query.forEach((q) => {
            if (Array.isArray(q) && q.length === 3) {
              ref = ref.where(q[0], q[1], q[2]);
            }
          });
        } else if (Array.isArray(query) && query.length === 3) {
          ref = ref.where(query[0], query[1], query[2]);
        }
      }

      if (ordine && Array.isArray(ordine)) {
        ref = ref.orderBy(...ordine);
      }

      if (typeof limit === "number" && limit > 0) {
        ref = ref.limit(limit);
      }

      if (realtime === false) {
        ref
          .get()
          .then((snapshot) => {
            const results = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setDocuments(results);
            setError(null);
          })
          .catch((err) => {
            setError("impossibile recuperare i dati dal database");
          });
        return () => {};
      } else {
        const unSub = ref.onSnapshot(
          (snapshot) => {
            const results = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setDocuments(results);
            setError(null);
          },
          (err) => {
            setError("impossibile recuperare i dati dal database");
          },
        );
        return () => unSub();
      }
    } catch (err) {
      setError("impossibile recuperare i dati dal database");
      return () => {};
    }
  }, [collection, queryKey, ordineKey, limitKey, realtimeKey]);

  return { documents, error };
};
